import { useEffect, useRef, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { t } from '@app/i18n';
import { useCanOperar } from '@app/auth/useCan';
import { Alert, Card, ConfirmDialog, DataTable, ErrorBanner, PageHeader } from '@zarya/ui';
import type { DataTableColumn } from '@zarya/ui';
import { Icon } from '@zarya/theme';
import { useEnvironments } from '../TenantSettings/TenantSettings.api';
import type { Environment } from '../TenantSettings/TenantSettingsModel';
import { ApprovalWorkflowCard } from './ApprovalWorkflowCard';
import { useEnvironmentApprovalRules } from './ApprovalWorkflow.api';
import { APPROVAL_EVENTS, effectiveRules, workflowLink } from './ApprovalWorkflowModel';
import type { ApprovalEvent } from './ApprovalWorkflowModel';
import s from './ApprovalWorkflow.module.css';

/**
 * A célula de um evento. A leitura é UMA por ambiente e serve as três colunas — o
 * react-query junta-as pela chave, então os três ganchos custam uma requisição.
 *
 * `first` existe porque o `DataTable` não tem `colSpan`: a falha e o «carregando» são
 * da LINHA, não da célula, e sem isto o mesmo banner apareceria três vezes por linha.
 */
function RuleCell({
  environment,
  event,
  first,
}: {
  environment: Environment;
  event: ApprovalEvent;
  first: boolean;
}) {
  const query = useEnvironmentApprovalRules(environment.id);

  if (query.isError) {
    return first ? (
      <ErrorBanner
        error={query.error}
        title={t('approvalWorkflow.error.read')}
        onRetry={() => void query.refetch()}
        hideLink
      />
    ) : null;
  }
  if (!query.data) return first ? <>{t('approvalWorkflow.loading')}</> : null;

  const rule = effectiveRules(query.data.rules).find((candidate) => candidate.event === event);
  if (!rule) return null;
  return (
    <>
      {t(`approvalWorkflow.mode.${rule.mode}`)}
      {rule.mode === 'granular' ? (
        <span className={s.ruleClasses}>
          {rule.classes.length ? rule.classes.join(', ') : t('approvalWorkflow.classes.empty')}
        </span>
      ) : null}
    </>
  );
}

export function ApprovalWorkflowPage() {
  const query = useEnvironments();
  const canEdit = useCanOperar('updateEnvironmentApprovalRules');
  const navigate = useNavigate();
  const [editorState, setEditorState] = useState({ dirty: false, pending: false });
  const [confirmDiscard, setConfirmDiscard] = useState(false);
  const [params] = useSearchParams();
  const target = params.get('environment');
  const environment = query.data?.items.find((item) => item.id === target);
  const environmentId = environment?.id;
  const targetRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (environmentId) targetRef.current?.focus({ preventScroll: true });
  }, [environmentId]);

  const columns: DataTableColumn<Environment>[] = [
    {
      key: 'environment',
      header: t('approvalWorkflow.column.environment'),
      cell: (item) => (
        <div className={s.environmentCell}>
          {item.name}
          <span className={s.slug}>{item.slug}</span>
          {!item.selectable_for_identity ? (
            <span className={s.help}>{t('approvalWorkflow.notSelectable')}</span>
          ) : null}
        </div>
      ),
    },
    ...APPROVAL_EVENTS.map((event, index) => ({
      key: event,
      header: t(`approvalWorkflow.event.${event}`),
      cell: (item: Environment) => (
        <RuleCell environment={item} event={event} first={index === 0} />
      ),
    })),
    {
      key: 'actions',
      header: t('approvalWorkflow.column.actions'),
      srOnlyHeader: true,
      width: '96px',
      // NAVEGA para a página das regras, logo continua `<a>`: um botão que muda de
      // rota tira do teclado e do menu do meio o que um link dá de graça. O glifo
      // acompanha o que a pessoa pode fazer — lápis para quem edita, lupa para quem
      // só lê —, e é o mesmo par das outras telas.
      cell: (item: Environment) => (
        <Link
          className={s.editLink}
          to={workflowLink(item.id)}
          aria-label={t(
            canEdit ? 'approvalWorkflow.editEnvironment' : 'approvalWorkflow.configure',
            { name: item.name },
          )}
        >
          <Icon name={canEdit ? 'pen-square' : 'file-search'} />
        </Link>
      ),
    },
  ];
  return (
    <>
      <PageHeader
        title={environment ? `${t('approvalWorkflow.page.title')} / ${environment.name}` : t('approvalWorkflow.page.title')}
        tip={t('approvalWorkflow.page.purpose')}
        icon="shield"
      />
      <div className={s.cards}>
        {query.isLoading ? <p>{t('approvalWorkflow.loading')}</p> : null}
        {query.isError ? (
          <ErrorBanner error={query.error} title={t('approvalWorkflow.error.environments')}
            onRetry={() => void query.refetch()} hideLink />
        ) : null}
        {query.data?.items.length === 0 ? <Alert tone="info" title={t('approvalWorkflow.empty')} /> : null}
        {query.data && target && !environment ? (
          <Alert tone="info" title={t('approvalWorkflow.missingEnvironment')} />
        ) : null}
        {environment ? (
          <div key={environment.id} ref={targetRef} tabIndex={-1} className={s.target}>
            <Link to="/approval-workflow" className={s.back}
              aria-disabled={editorState.pending}
              onClick={(event) => {
                if (editorState.pending || editorState.dirty) event.preventDefault();
                if (!editorState.pending && editorState.dirty) setConfirmDiscard(true);
              }}>{t('approvalWorkflow.back')}</Link>
            <div role="tablist" aria-label={t('approvalWorkflow.page.title')} className={s.tabs}>
              <button type="button" role="tab" id="approval-rules-tab" aria-selected="true"
                aria-controls="approval-rules-panel" className={s.tab}>
                {t('approvalWorkflow.tab.rules')}
              </button>
            </div>
            <div role="tabpanel" id="approval-rules-panel" aria-labelledby="approval-rules-tab">
              <ApprovalWorkflowCard environment={environment} onStateChange={setEditorState} />
            </div>
          </div>
        ) : query.data?.items.length ? (
          // O `Card bare` é o que dá o fundo branco das outras grades (Sites, T23):
          // a tabela à mão que vivia aqui não o tinha, e a tela ficava a flutuar sobre
          // o fundo da página. `bare` porque a `DataTable` já traz o seu espaçamento.
          <Card bare aria-label={t('approvalWorkflow.table.card.label')}>
            <DataTable<Environment>
              label={t('approvalWorkflow.page.title')}
              columns={columns}
              rows={query.data.items}
              rowKey={(item) => item.id}
            />
          </Card>
        ) : null}
        <Card title={t('approvalWorkflow.fixed.title')}>
          <ul className={s.fixed}>
            <li>{t('approvalWorkflow.fixed.codeOnly')}</li>
            <li>{t('approvalWorkflow.fixed.noEnvironment')}</li>
            <li>{t('approvalWorkflow.fixed.unknown')}</li>
          </ul>
        </Card>
      </div>
      <ConfirmDialog open={confirmDiscard}
        title={t('common.ui.discardTitle')} message={t('common.ui.discard')}
        confirmLabel={t('common.ui.discardConfirm')} cancelLabel={t('common.ui.keepEditing')}
        destructive onCancel={() => setConfirmDiscard(false)}
        onConfirm={() => {
          setConfirmDiscard(false);
          setEditorState({ dirty: false, pending: false });
          navigate('/approval-workflow');
        }} />
    </>
  );
}
